import { checkAppealsBulkFlow, checkIsInternal, getTinGroupId } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import {
  RC_VOLUME_DATA_API_URL,
  RC_VOLUME_DATA_BULK_API_URL,
  RC_AVERAGE_DATA_API_URL,
  RC_AVERAGE_DATA_BULK_API_URL,
  RC_OUTCOME_VOLUME_DATA_API_URL,
  RC_OUTCOME_SUBMISSION_DATA_API_URL,
  RC_DETAILS_API_URL,
} from './apiUrlConstants';
import { RECONSIDERATION_DETAILS_DATA_API_URL } from './apiUrlConstants';
import { pollBulkJob, getJobId, startJobPolling } from '@src/utility/CommonBulkPoll';
import { getContractIdValuesFromSession } from '@src/CommonComponents/ContractIdFilter/contractIdFilter';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const _httpService = new HttpService();
const cache = new Map();

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL') ?? '');
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL') ?? '');
}

function getReconsiderationsEndpoint(
  endpoint: string,
  isBulk: boolean = false
): string {
  return isBulk
    ? `claims-bulk-process-jobs/${endpoint}`
    : `${endpoint}/`;
}

const isContractIdBulkFlow = () => (
  sessionStorage.getItem('contractIdNavigation') === 'true'
  && sessionStorage.getItem('hiVolumeContractFlow') === 'true'
);

const getBulkRequestPayload = (payload: any) => {
  if (!isContractIdBulkFlow()) {
    return payload;
  }

  const contractIds = getContractIdValuesFromSession();
  if (contractIds.length === 0) {
    return payload;
  }

  const requestPayload = {
    ...payload,
    ContractIds: contractIds,
  };
  delete requestPayload.Tin;
  delete requestPayload.Tins;
  delete requestPayload.ProviderSysKey;
  return requestPayload;
};

/**
 * Common method to handle reconsideration data fetching with bulk and non-bulk flows
 * @param url - API endpoint URL
 * @param payload - Request payload
 * @param errorContext - Context message for error logging
 * @returns Response data or null on error
 */
const fetchReconsiderationData = async (
  url: string,
  payload: any,
) => {
  const isBulkFlow = checkAppealsBulkFlow();
  const finalUrl = getReconsiderationsEndpoint(url, isBulkFlow);

  if (isBulkFlow) {
    try {
      const pollingResult = await pollBulkJob<any>(finalUrl, getBulkRequestPayload(payload));
      return { StatusCode: 200, Data: pollingResult };
    } catch (error) {
      console.error(`Error occurred while polling for ${finalUrl}:`, error);
      return null;
    }
  } else {
    try {
      const responseData: any = await _httpService.push(finalUrl, payload);
      return responseData;
    } catch {
      return null;
    }
  }
};

export const getReconsiderationPendingVolData = async (payload: any, highVolumeTinFlag?: boolean) => {
  // Check if this is a high volume TIN flow (bulk processing)
  const isBulkFlow = highVolumeTinFlag === true || checkAppealsBulkFlow();

  // If bulk flow, use the bulk API endpoint, otherwise use standard endpoint
  const url = isBulkFlow ? RC_VOLUME_DATA_BULK_API_URL : RC_VOLUME_DATA_API_URL;

  // For bulk flow, add Type parameter and ensure TinGroupId is used
  let requestPayload = payload;
  if (isBulkFlow) {
    requestPayload = {
      TinGroupId: getTinGroupId(),
      ...getBulkRequestPayload(payload),
      Type: 'Volume'
    };
    // pending-metrics uses OverturnPass, use it if ReconPass exists
    if (requestPayload.ReconPass && !requestPayload.OverturnPass) {
      requestPayload.OverturnPass = requestPayload.ReconPass;
    }
    delete requestPayload.ReconPass;
    delete requestPayload.Tin; // Remove Tin for bulk flow
  }

  if (isBulkFlow) {
    try {
      // Submit job and get JobId
const jobIdResponse = await getJobId(url, requestPayload);
      const jobId = jobIdResponse.Data.JobId;
// Poll for job completion using startJobPolling
      return new Promise((resolve, reject) => {
        startJobPolling(
          jobId,
          {
            onInProgress: () => {
},
            onCompleted: (result) => {
              resolve({ StatusCode: 200, Data: result });
            },
            onFailed: (errorMessage) => {
              console.error('[RC Volume] Bulk job polling - Job failed:', errorMessage);
              reject(new Error(errorMessage));
            },
            onStatusUpdate: (statusResponse) => {
},
          },
          '/job-status/result',
          1000,
          undefined,
          false
        );
      });
    } catch (error) {
      console.error('[RC Volume] Error in bulk flow:', error);
      return null;
    }
  } else {
    try {
      const responseData: any = await _httpService.push(url, requestPayload);
      return responseData;
    } catch {
      return null;
    }
  }
};

export const getReconsiderationAverageData = async (payload: any, highVolumeTinFlag?: boolean) => {
  // Check if this is a high volume TIN flow (bulk processing)
  const isBulkFlow = highVolumeTinFlag === true || checkAppealsBulkFlow();

  // If bulk flow, use the bulk API endpoint, otherwise use standard endpoint
  const url = isBulkFlow ? RC_AVERAGE_DATA_BULK_API_URL : RC_AVERAGE_DATA_API_URL;

  // For bulk flow, add Type parameter and ensure TinGroupId is used
  let requestPayload = payload;
  if (isBulkFlow) {
    requestPayload = {
      TinGroupId: getTinGroupId(),
      ...getBulkRequestPayload(payload),
      Type: 'Average'
    };
    // Remove Tin for bulk flow
    delete requestPayload.Tin;
  }

  if (isBulkFlow) {
    try {
      // Submit job and get JobId
const jobIdResponse = await getJobId(url, requestPayload);
      const jobId = jobIdResponse.Data.JobId;
// Poll for job completion using startJobPolling
      return new Promise((resolve, reject) => {
        startJobPolling(
          jobId,
          {
            onInProgress: () => {
},
            onCompleted: (result) => {
resolve({ StatusCode: 200, Data: result });
            },
            onFailed: (errorMessage) => {
              console.error('[RC Average] Bulk job polling - Job failed:', errorMessage);
              reject(new Error(errorMessage));
            },
            onStatusUpdate: (statusResponse) => {
},
          },
          '/job-status/result',
          1000,
          undefined,
          false
        );
      });
    } catch (error) {
      console.error('[RC Average] Error in bulk flow:', error);
      return null;
    }
  } else {
    try {
      const responseData: any = await _httpService.push(url, requestPayload);
      return responseData;
    } catch {
      return null;
    }
  }
};

export const getReconsiderationOutcomeReasonsData = async (payload: any) => {
  return fetchReconsiderationData(
    RC_OUTCOME_VOLUME_DATA_API_URL,
    payload,
  );
};

export const getOutcomeSubmissionReasons = async (payload: any) => {
  return fetchReconsiderationData(
    RC_OUTCOME_SUBMISSION_DATA_API_URL,
    payload,
  );
};

export const getReconsiderationDetails = async (payload: any) => {
  return fetchReconsiderationData(
    RC_DETAILS_API_URL,
    payload,
  );
};

export const getReconsiderationData = async (payload: any) => {
  const url = RECONSIDERATION_DETAILS_DATA_API_URL;
  let finalUrl = url;

  if (payload?.queryParam && typeof payload.queryParam === 'object') {
    const queryParams = new URLSearchParams(payload.queryParam).toString();
    if (queryParams) {
      finalUrl = `${url}?${queryParams}`;
    }
  }

  return fetchReconsiderationData(
    finalUrl,
    payload,
  );
};
